import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { useUniversalPreset } from '@atlaskit/editor-core/preset-universal';
import type { EditorProps } from '@atlaskit/editor-core/editor-props';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import type { Providers } from '@atlaskit/editor-common/provider-factory';
import { ModalTransition } from '@atlaskit/modal-dialog';
import {
  FormEvent,
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { TitleInput } from './TitleInput';
import { BreadcrumbActions } from './BreadcrumbActions';
import { useDocumentStorage } from '../providers/document-storage';
import { TemplatesSidebar } from './TemplatesSidebar';
import { useEditorActions } from '../providers/actions-provider';
import { getEmojiProvider } from '../providers/emoji/get-emoji-provider';
import { getSandboxTaskDecisionResource } from '../providers/task-decision/sandbox-task-decision-data';
import { mentionResourceProvider } from '../providers/mentions/sandbox-mention-resource';
import { SignupFeatureModal } from './SignupFeatureModal';
import { ShareModal } from './ShareModal';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import {
  type ActionSignup,
  useSandboxActions
} from '../providers/sandbox-actions';
import { fakeExtension } from '../constants/fake-extension';
import { Template } from '../constants/templates';
import { ConfluenceIcon } from '@atlaskit/logo';
import { getAnalyticsWebClient } from '../utils/analytics';
import { isVersion } from '../utils/version';
import ShareButton from './ShareButtonGroups';

let changeEventFired = false;
// Match the full-page layout used when custom primary toolbar components exist.
const sandboxEditorFeatureFlags = {
  tableCellOptionsInFloatingToolbar: true,
  twoLineEditorToolbar: true
};

const SandboxComposableEditor = (props: EditorProps) => {
  const preset = useUniversalPreset({ props });
  return <ComposableEditor preset={preset} {...props} />;
};

export const extensionHandlers = (signup: ActionSignup) => {
  return {
    'com.atlassian.confluence.macro.core': {
      render: fakeExtension(signup)
    }
  } satisfies ExtensionHandlers;
};

export const getProviders = (): Partial<Providers> => ({
  emojiProvider: getEmojiProvider(),
  taskDecisionProvider: Promise.resolve(getSandboxTaskDecisionResource()),
  mentionProvider: Promise.resolve(mentionResourceProvider),
  extensionProvider: Promise.resolve(new DefaultExtensionProvider([]))
});

const SAVE_ACTION = () => void 0;

interface SandboxEditorProps {
  setMode?: (mode: boolean) => void;
}

type ModalType = 'signup' | 'share';

interface ModalState {
  isOpen: boolean;
  type: ModalType | null;
}

export const SandboxEditor = ({}: SandboxEditorProps) => {
  const {
    document,
    title,
    setTitle,
    appearance,
    setAppearance,
    currentTemplateId
  } = useDocumentStorage();
  const { signup, setActions, share } = useSandboxActions();
  const providers = useRef(getProviders());
  const actions = useEditorActions();
  const [titleFocused, setTitleFocused] = useState<boolean>(false);
  const [shouldFocusEditor, setShouldFocusEditor] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: null
  });
  const analyticsClient = getAnalyticsWebClient();

  setActions(actions);

  const changeTemplate = useCallback((template: Template) => {
    currentTemplateId.current = template.id;
    actions.clear();
    actions.replaceDocument(template.template, false);
    setTitle('');
  }, []);

  const handleTitleChange = (e: FormEvent<HTMLTextAreaElement>) => {
    const title = (e.target as HTMLTextAreaElement).value;
    setTitle(title);
  };

  const setFullWidthMode = (fullWidthMode: boolean) => {
    setAppearance(fullWidthMode ? 'full-width' : 'full-page');
  };

  useEffect(() => {
    // focus editor ONLY when forced to.
    // Losing title focus should NOT require forcing an editor focus
    // (in the case of backtabbing or clicking on a different button)
    if (!titleFocused && shouldFocusEditor) {
      actions.focus();
    }
  }, [actions, titleFocused, shouldFocusEditor]);

  // This function is only called when "tab" or "enter" is pressed when focused in the title
  const focusEditor = useCallback(() => {
    setTitleFocused(false);
    setShouldFocusEditor(true);
  }, []);

  const onFocusChange = useCallback((isTitleFocused: boolean) => {
    setTitleFocused(isTitleFocused);
    // we disable editor when title is focused
    // re-enabling editor leads to force focus set on it
    // this leads to focus bugs when clicking on other inputs
    // so we explicitly disable editor focus here
    setShouldFocusEditor(false);
  }, []);

  const onKeyPressed = useCallback(
    (e: KeyboardEvent) => {
      // Shift+Tab should not be prevented, so we check if tab pressed without shift
      const isTabPressed = e.key === 'Tab' && !e.shiftKey;
      const isEnterPressed = e.key === 'Enter';

      if (isTabPressed || isEnterPressed) {
        e.preventDefault();
        e.stopPropagation();
        focusEditor();
      }
    },
    [focusEditor]
  );

  const handleTitleOnFocus = () => {
    onFocusChange(true);
  };
  const handleTitleOnBlur = () => {
    onFocusChange(false);
  };

  return (
    <>
      <SandboxComposableEditor
        onChange={() => {
          if (!changeEventFired) {
            analyticsClient.sendUIEvent({
              action: 'clicked',
              actionSubject: 'sandboxEditorChange',
              source: 'confluenceSandbox'
            });
            changeEventFired = true;
          }
        }}
        allowAnalyticsGASV3={true}
        allowBreakout
        allowDate
        allowExpand={{
          allowInsertion: true,
          allowInteractiveExpand: true
        }}
        allowExtension={{
          allowBreakout: true,
          allowExtendFloatingToolbars: true
        }}
        allowFindReplace={{
          allowMatchCase: true
        }}
        allowIndentation
        allowLayouts={{
          allowBreakout: true,
          UNSAFE_addSidebarLayouts: true,
          UNSAFE_allowSingleColumnLayout: true
        }}
        allowNestedTasks
        allowPanel
        allowRule
        allowStatus
        allowTables={{
          allowBackgroundColor: true,
          allowColumnResizing: true,
          allowColumnSorting: true,
          allowDistributeColumns: true,
          allowHeaderColumn: true,
          allowHeaderRow: true,
          allowMergeCells: true,
          allowNumberColumn: true,
          allowControls: true,
          permittedLayouts: 'all'
        }}
        allowTemplatePlaceholders
        allowTextAlignment
        allowTextColor
        allowUndoRedoButtons
        waitForMediaUpload
        codeBlock={{
          allowCopyToClipboard: true
        }}
        allowHelpDialog
        placeholder="Type / to add elements or @ to mention someone. Use markdown shortcuts to format your page as you type, like * for lists, # for headers, and *** for a horizontal rule."
        placeholderBracketHint="Did you mean to use '/' to insert content?"
        shouldFocus={shouldFocusEditor}
        disabled={titleFocused}
        defaultValue={document}
        media={{
          allowMediaSingle: true,
          allowMediaGroup: true
        }}
        contentComponents={
          <Fragment>
            <BreadcrumbActions
              appearance={appearance}
              onFullWidthChange={setFullWidthMode}
            />
            <TitleInput
              value={title}
              onChange={handleTitleChange}
              onFocus={handleTitleOnFocus}
              onBlur={handleTitleOnBlur}
              onKeyDown={(e) => {
                onKeyPressed(e);
              }}
            />
          </Fragment>
        }
        // primaryToolbarComponents={[
        //   <SignupAndSaveButtons key={1} signup={() => signup(false, actions)} />
        // ]}
        primaryToolbarComponents={
          isVersion(2)
            ? [
                <ShareButton
                  key="share"
                  onShare={() => {
                    setModalState({ isOpen: true, type: 'share' });
                  }}
                  onCopyLink={share}
                />
              ]
            : []
        }
        contextPanel={<TemplatesSidebar onTemplateChange={changeTemplate} />}
        onSave={SAVE_ACTION}
        insertMenuItems={[]}
        performanceTracking={{
          ttiTracking: {
            enabled: false
          },
          transactionTracking: { enabled: false },
          uiTracking: { enabled: false },
          inputTracking: {
            enabled: false
          },
          bFreezeTracking: {
            enabled: false
          },
          // proseMirrorRenderedTracking: {
          //   enabled: false
          // },
          contentRetrievalTracking: {
            enabled: false
          }
        }}
        extensionHandlers={extensionHandlers(signup)}
        {...providers.current}
        // {...editorProps}
        featureFlags={sandboxEditorFeatureFlags}
        appearance={appearance as EditorAppearance}
        legacyImageUploadProvider={Promise.resolve(() => {
          setModalState({ isOpen: true, type: 'signup' });
        })}
        primaryToolbarIconBefore={
          <span>
            <ConfluenceIcon appearance="brand" />
          </span>
        }
      />
      <ModalTransition>
        {modalState.isOpen && modalState.type === 'signup' && (
          <SignupFeatureModal
            onClose={(accept: boolean) => {
              if (accept) {
                signup(true);
              }
              setModalState({ isOpen: false, type: null });
            }}
          />
        )}
        {modalState.isOpen && modalState.type === 'share' && (
          <ShareModal
            onClose={(accept: boolean) => {
              if (accept) {
                share();
              }
              setModalState({ isOpen: false, type: null });
            }}
          />
        )}
      </ModalTransition>
    </>
  );
};
